/*eslint-disable camelcase*/

const _window = typeof window !== 'undefined' ?
  window : typeof global !== 'undefined' ?
  global : typeof self !== 'undefined' ? self : {};

export default class ErrorHandler {
  constructor({swallowExceptions}) {
    this.swallowExceptions = swallowExceptions;
    this.listeners = {};

    //*************************************************************************
    // We are wrapping the browser's native methods with try/catch for several
    // reasons:
    //
    // 1. It provides us with a more elaborate stack trace.
    //    Currently we ignore the stack trace due to BI limitations, but it can
    //    be very useful for future support.
    //
    // 2. Makes it easier to classify the error types.
    //
    // 3. Some browsers don't support a global error handler.
    //*************************************************************************

    this.instrumentAjax();
    this.instrumentSetTimeout();
    this.instrumentSetInterval();
    this.instrumentAddEventListener();
    this.instrumentFetch();
    this.instrumentPromise();

    //Sometimes catches one of the instruments above? Need to verify...
    this.instrumentGloballyUnhandledError();
  }

  subscribeListener(appName, cb) {
    this.listeners[appName] = cb;
  }

  removeListener(appName) {
    delete this.listeners[appName];
  }

  instrumentAjax() {
    const self = this;

    if ('XMLHttpRequest' in _window) {
      const xhrproto = XMLHttpRequest.prototype;

      this._wrap(xhrproto, 'open', orig =>
          function (method, url) {
            this.__fedops_xhr = {
              method,
              url,
              status_code: null
            };

            return orig.apply(this, arguments);
          }
        , 'xhr');

      this._wrap(xhrproto, 'send', orig =>
        function () {
          const xhr = this;

          function saveStatusCode() {
            if (xhr.readyState === 1 || xhr.readyState === 4) {
              try {
                xhr.__fedops_xhr.status_code = xhr.status;
              } catch (e) {
              }

              if (self._isErrorStatusCode(xhr.__fedops_xhr.status_code)) {
                self._reportError(xhr.__fedops_xhr, 'xhr', xhr.__fedops_xhr.status_code);
              }
            }
          }

          self._wrap(this, 'onload', orig =>
              function () {
                orig && orig.apply(this, arguments);
              }
            , 'xhr');

          self._wrap(this, 'onerror', orig =>
              function () {
                orig && orig.apply(this, arguments);
              }
            , 'xhr');

          self._wrap(this, 'onprogress', orig =>
              function () {
                orig && orig.apply(this, arguments);
              }
            , 'xhr');

          if ('onreadystatechange' in xhr && typeof xhr.onreadystatechange === 'function') {
            self._wrap(xhr, 'onreadystatechange', orig =>
              function () {
                saveStatusCode();
                return orig.apply(this, arguments);
              });
          } else {
            xhr.onreadystatechange = saveStatusCode;
          }

          return orig.apply(this, arguments);
        }, 'xhr'
      )
      ;
    }
  }

  instrumentSetTimeout() {
    const self = this;

    this._wrap(_window, 'setTimeout', globalSetTimeout =>
      function (fn, ...rest) {
        return globalSetTimeout.call(this, self._wrapCallback(fn, 'setTimeout'), ...rest);
      });
  }

  instrumentSetInterval() {
    const self = this;

    this._wrap(_window, 'setInterval', globalSetInterval =>
      function (fn, ...rest) {
        return globalSetInterval.call(this, self._wrapCallback(fn, 'setInterval'), ...rest);
      });
  }

  instrumentAddEventListener() {
    const self = this;

    function instrumentEventTarget(name) {
      const origTarget = _window[name] && _window[name].prototype;

      if (origTarget && Object.prototype.hasOwnProperty.call(origTarget, 'addEventListener')) {
        self._wrap(origTarget, 'addEventListener', orig =>
          function (eventName, fn, capture, secure) {
            const wrapped = self._wrapCallback(fn, 'addEventListener');

            if (fn) {
              fn.__fedopsWrapper__ = wrapped;
            }

            return orig.call(this, eventName, wrapped, capture, secure);
          });

        self._wrap(origTarget, 'removeEventListener', orig =>
          function (eventName, fn, capture, secure) {
            fn = fn && (fn.__fedopsWrapper__ || fn);
            return orig.call(this, eventName, fn, capture, secure);
          });
      }
    }

    const eventTargets = ['EventTarget', 'Window', 'Node', 'ApplicationCache', 'AudioTrackList', 'ChannelMergerNode', 'CryptoOperation', 'EventSource', 'FileReader', 'HTMLUnknownElement', 'IDBDatabase', 'IDBRequest', 'IDBTransaction', 'KeyOperation', 'MediaController', 'MessagePort', 'ModalWindow', 'Notification', 'SVGElementInstance', 'Screen', 'TextTrack', 'TextTrackCue', 'TextTrackList', 'WebSocket', 'WebSocketWorker', 'Worker', 'XMLHttpRequest', 'XMLHttpRequestEventTarget', 'XMLHttpRequestUpload'];
    eventTargets.forEach(name => instrumentEventTarget(name));
  }

  instrumentRequestAnimationFrame() {

  }

  instrumentFetch() {
    const self = this;

    this._wrap(_window, 'fetch', globalFetch =>
      function (url, fetchOptions) {
        return globalFetch.apply(this, arguments).then(response => {
          const statusCode = response.status;

          if (self._isErrorStatusCode(statusCode)) {
            const requestInfo = {
              url,
              method: fetchOptions ? fetchOptions.method : 'GET',
              status_code: statusCode
            };

            self._reportError(requestInfo, 'fetch', statusCode);
          }

          return response;
        });
      });
  }

  instrumentPromise() {
    const self = this;

    _window.addEventListener('unhandledrejection', err => {
      self._reportError(err, 'promise');
    });
  }

  instrumentGloballyUnhandledError() {
    _window.addEventListener('error', err => {
      this._reportError(err, 'windowOnError');
    });
  }

  instrumentHistory() {

  }

  _wrap(obj, fnName, replaced, errorType) {
    const self = this;
    const orig = obj[fnName];
    const r = replaced(orig);

    const wrapped = function () {
      try {
        return r.apply(this, arguments);
      } catch (err) {
        self._reportError(err, errorType);

        if (!self.swallowExceptions) {
          throw err;
        }
      }
    };

    obj[fnName] = wrapped;
  }

  _wrapCallback(fn, errorType) {
    const self = this;

    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (err) {
        self._reportError(err, errorType);

        if (!self.swallowExceptions) {
          throw err;
        }
      }
    };
  }

  _reportError(err, errorType, errorInfo) {
    const self = this;

    const actualError = err && (err.error || err);

    if (!actualError.__fedopsHandledError__) {
      const appNames = Object.keys(this.listeners);

      appNames.forEach(appName => {
        self.listeners[appName](actualError, errorType, errorInfo);
      });

      actualError.__fedopsHandledError__ = true;
    }
  }

  _isErrorStatusCode(statusCode) {
    return statusCode >= 400 || statusCode === 0;
  }
}

/*eslint-enable camelcase*/



// WEBPACK FOOTER //
// ./error-handler.js